import { useEffect, useState } from 'react'
import { Badge, Col, Container, Row, Table, Card } from 'react-bootstrap'
import { Link, useLocation, useParams } from 'react-router-dom'
import BreadCrumb from '../../../components/Label/Breadcrumb'
import api from '../../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../../helpers/constants'
import * as Common from '../../../utils/Common'
import { Copy } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const DetailDeliveryHistory = () => {
  const { id } = useParams()
  const { state } = useLocation()

  const [detail, setDetail] = useState({})
  const [sjList, setSjList] = useState([])
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await api.get(`outbound/delivery_history/detail/${id}`)
        const data = res.data.data
        setDetail(data)
        setSjList(data.sj_draft || [])
      } catch (err) {
        const msg = err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(msg)
      }
    }

    fetchDetail()
  }, [id])

  const renderInput = (label, value, col = 3, unit = '') => (
    <Col md={col}>
      <small style={labelStyle}>{label}</small>
      <h5 className="mt-1" style={{ fontSize: '1.1rem' }}>
        {value ?? '-'}
        {unit}
      </h5>
    </Col>
  )

  const renderStatusBadge = (status) => {
    const normalized = status?.toLowerCase()
    if (normalized === 'done') {
      return <Badge bg="success">DONE</Badge>
    }
    return <Badge bg="primary">ON DELIVERY</Badge>
  }

  return (
    <>
      <Container fluid className="px-4 mb-5">
        <Row className="mb-4">
          <Col>
            <BreadCrumb
              label={detail?.plate_no || ''}
              link="/outbound/delivery-history"
              back="Delivery History"
              stateValue={state}
            />
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="mb-4">
            {renderInput('Driver', detail.driver)}
            {renderInput('Plate No', detail.plate_no)}
            {renderInput('Capacity (kg)', detail.capacity)}
            {renderInput('Vehicle Type', detail.vehicle_type)}
          </Row>
          <Row>
            {renderInput(
              'Total Qty KG/ Outbound Plan',
              detail.total_qty_outbound
                ? parseFloat(detail.total_qty_outbound).toLocaleString('id-ID')
                : '0',
              3
            )}
            {renderInput(
              'Total SJ in Outbound Plan',
              detail.total_sj ? detail.total_sj.toLocaleString('id-ID') : '0',
              3
            )}
            {renderInput(
              'Total Item in Outbound Plan',
              detail.total_item_outbound
                ? detail.total_item_outbound.toLocaleString('id-ID')
                : '0',
              3
            )}
          </Row>
        </Card>

        <Card style={cardFilterStyle}>
          <Row>
            <Col md={12} className="mb-3">
              <Link
                to={`/outbound/plan/detail/${detail.outbound_id}`}
                target="_blank"
                className="text-decoration-none"
              >
                <h5 style={headerTitleStyle}>{detail.outbound || '-'}</h5>
              </Link>
            </Col>
            <Col md={12}>
              <Table
                hover
                responsive
                className="shadow-sm bg-white"
                style={{
                  fontSize: '13px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: 0
                }}
              >
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="p-2 align-middle" style={tableHeaderStyle}>
                      #SJ
                    </th>
                    <th className="p-2 align-middle" style={tableHeaderStyle}>
                      Departure Date
                    </th>
                    <th className="p-2 align-middle" style={tableHeaderStyle}>
                      Delivery Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sjList.length > 0 ? (
                    sjList.map((item) => (
                      <tr key={item.draft_code}>
                        <td className="p-2 align-middle">
                          <div className="d-flex align-items-center gap-2">
                            {item.draft_code}
                            <Copy
                              size={14}
                              className="text-muted cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                opacity: 0.5,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = 1)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = 0.5)
                              }
                              onClick={() => handleCopy(item.draft_code)}
                              title="Copy SJ Number"
                            />
                          </div>
                        </td>
                        <td className="p-2 align-middle">
                          {item.departure_date
                            ? convertDateWithoutTime(item.departure_date)
                            : '-'}
                        </td>
                        <td className="p-2 align-middle">
                          {renderStatusBadge(item.delivery_status)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-3 text-center fw-bold" colSpan={3}>
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card>
      </Container>
      <Toaster />
    </>
  )
}

export default DetailDeliveryHistory
